import api from './api';
import jwt_decode from 'jwt-decode';

/**
 * Authentication service for handling all auth-related API calls and token management
 */
const authService = {
  /**
   * Login user with username and password
   * @param {string} username - The username
   * @param {string} password - The password
   * @returns {Promise<Object>} User data if login successful
   */
  login: async (username, password) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      const { token, refreshToken } = response.data;
      
      // Store tokens in localStorage
      localStorage.setItem('auth_token', token);
      
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      
      // Decode token to get user info
      const user = authService.getUserFromToken(token);
      
      return user;
    } catch (error) {
      // Transform error message if possible
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      throw new Error(errorMessage);
    }
  },
  
  /**
   * Logout current user
   */
  logout: () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
  },
  
  /**
   * Check if user is authenticated (has valid token)
   * @returns {boolean} True if authenticated
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('auth_token');
    if (!token) return false;
    
    try {
      const decodedToken = jwt_decode(token);
      const currentTime = Date.now() / 1000;
      
      // Check if token is expired
      return decodedToken.exp > currentTime;
    } catch (error) {
      return false;
    }
  },
  
  /**
   * Get current auth token
   * @returns {string|null} The auth token or null if not authenticated
   */
  getToken: () => {
    return localStorage.getItem('auth_token');
  },
  
  /**
   * Extract user information from JWT token
   * @param {string} token - The JWT token
   * @returns {Object} User information
   */
  getUserFromToken: (token) => {
    try {
      const decodedToken = jwt_decode(token);
      return {
        id: decodedToken.id,
        username: decodedToken.username,
        email: decodedToken.email,
        role: decodedToken.role
      };
    } catch (error) {
      throw new Error('Invalid token');
    }
  },
  
  /**
   * Get current user information
   * @returns {Object|null} User information or null if not authenticated
   */
  getCurrentUser: () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return null;
      
      return authService.getUserFromToken(token);
    } catch (error) {
      return null;
    }
  },
  
  /**
   * Verify token validity with the server
   * @returns {Promise<boolean>} True if token is valid
   */
  verifyToken: async () => {
    try {
      const response = await api.get('/auth/verify');
      return response.status === 200;
    } catch (error) {
      return false;
    }
  },
  
  /**
   * Refresh the auth token using the refresh token
   * @returns {Promise<Object>} New tokens and user data
   */
  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await api.post('/auth/refresh', { refreshToken });
      const { token, refreshToken: newRefreshToken } = response.data;
      
      // Store new tokens
      localStorage.setItem('auth_token', token);
      
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }
      
      // Return user data from new token
      return authService.getUserFromToken(token);
    } catch (error) {
      // Clear tokens on refresh failure
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      throw new Error('Token refresh failed');
    }
  }
};

export default authService;